import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";

/**
 * /qr/:token — universal QR landing route.
 * Resolves token → player. If viewer is admin, redirect to admin player profile.
 * Otherwise redirect to public prospect page.
 */
export default function QRLanding() {
  const { token } = useParams();
  const { user, isAdmin, loading } = useAuth();
  const [resolved, setResolved] = useState(null); // { player } | "not_found"

  useEffect(() => {
    if (loading) return;
    api
      .get(`/qr/lookup/${token}`)
      .then(({ data }) => setResolved(data))
      .catch(() => setResolved("not_found"));
  }, [token, loading]);

  if (loading || resolved === null) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10 label-overline">Resolving credential…</div>
      </PublicLayout>
    );
  }

  if (resolved === "not_found" || !resolved?.player) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Invalid QR Code</div>
          <p className="text-sm text-slate-600 mt-2">
            This credential token doesn&apos;t match any active player. Cards regenerated by CBR admins
            invalidate previous codes.
          </p>
        </div>
      </PublicLayout>
    );
  }

  if (user && isAdmin) {
    return <Navigate to={`/admin/players/${resolved.player.id}`} replace />;
  }
  if (user && user.role === "evaluator") {
    return <Navigate to={`/evaluate/${resolved.player.id}`} replace />;
  }
  return <Navigate to={`/prospects/${resolved.player.slug}`} replace />;
}
